import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosGet,
  axiosPost,
  reactQueryDefaultOptions,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

// Report Exclusions: the per-OE-period list of clients the admin dashboard
// chart counts and the status tracking report leave out.
//
// Demo clients - any client id below 1000 - are excluded by a rule in
// ReportExclusionFilter on the server, not by rows on this list, so they never
// appear in the grid.

// Rows for the admin grid, scoped to one OE period.
export const useQueryGetReportExclusions = (
  oePeriodId,
  includeInactive = false
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.REPORT_EXCLUSION_LIST_GET(oePeriodId, includeInactive)
    );
  };

  return useQuery(
    [QUERY_KEYS.REPORT_EXCLUSION_LIST, oePeriodId, includeInactive],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled: !!oePeriodId,
    }
  );
};

const invalidateReportExclusions = (queryClient, variables) => {
  // Both the active and the show-removed list for the period are stale after a
  // write, so invalidate on the period rather than the exact key.
  queryClient.invalidateQueries([
    QUERY_KEYS.REPORT_EXCLUSION_LIST,
    variables?.oePeriodId,
  ]);
};

/**
 * Bulk add / reactivate.
 *
 * Resolves with { addedCount, reactivatedCount, alreadyExcludedCount,
 * invalidCount, demoClientCount, outcomes }. A demo client comes back as
 * demoClientCount rather than an error: it is already excluded by rule, so no
 * row is stored for it.
 */
export const useMutatePostReportExclusionClients = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.REPORT_EXCLUSION_CLIENTS_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateReportExclusions(queryClient, variables),
  });
};

// Puts one client back on the reports. Resolves with true when a row was
// actually removed; an already-removed client resolves false rather than
// throwing.
export const useMutatePostReportExclusionRemove = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.REPORT_EXCLUSION_REMOVE_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (result, variables) =>
      invalidateReportExclusions(queryClient, variables),
  });
};
